import { eLayoutType, RoutesService, ABP, ConfigStateService } from '@abp/ng.core';
import { provideAppInitializer, inject } from '@angular/core';
import { MenuGeneratorService } from './CBD/services/menu-generator.service';
import { MenuTransformService } from './CBD/services/menu-transform.service';
import { ICbdItem } from './CBD/interfaces/ICbdItem';
import { catchError, filter, of, take, timeout, firstValueFrom } from 'rxjs';

export const APP_ROUTE_PROVIDER = [
  provideAppInitializer(() => {
    const routes = inject(RoutesService);
    const menuGenerator = inject(MenuGeneratorService);
    const menuTransform = inject(MenuTransformService);
    const configState = inject(ConfigStateService);

    return firstValueFrom(
      configState.getOne$('auth').pipe(
        filter(auth => !!auth && !!auth.grantedPolicies),
        take(1),
        timeout({
          first: 5000,
          with: () => of(null),
        }),
        catchError(() => of(null))
      )
    ).then(() => {
      configureFilteredRoutes(routes, menuGenerator, menuTransform, configState);
    });
  }),
];

function configureFilteredRoutes(
  routes: RoutesService,
  menuGenerator: MenuGeneratorService,
  menuTransform: MenuTransformService,
  configState: ConfigStateService
) {
  const menuItems = menuGenerator.generateMenu();
  const grantedPolicies = configState.getDeep('auth.grantedPolicies') || {};
  const translatedItems = menuTransform.translateItems(menuItems);
  const sortedItems = menuTransform.sortAlphabetically(translatedItems);
  const routeNames = new Map<string, number>();

  const abpRoutes: ABP.Route[] = [];

  routes.add([
    {
      path: '/',
      name: 'LanguageService::Menu:CBD',
      iconClass: 'bi bi-house-door-fill',
      order: 1,
      layout: eLayoutType.application,
      requiredPolicy: 'AdministrationService.Home',
    },
  ]);

  sortedItems.forEach((category, categoryIndex) => {
    const categoryOrder = categoryIndex + 1;
    const categoryHasVisibleChildren = hasVisibleChildren(category, grantedPolicies);

    if (!categoryHasVisibleChildren) {
      return;
    }

    const categoryName = createUniqueRouteName(routeNames, category.title);

    abpRoutes.push({
      path: '',
      name: categoryName,
      order: categoryOrder,
      invisible: false,
      layout: eLayoutType.application,
    });

    category.subcategories?.forEach((subcategory, subcategoryIndex) => {
      const subcategoryOrder = categoryOrder + (subcategoryIndex + 1) * 100;
      const subcategoryName = subcategory.title
        ? createUniqueRouteName(routeNames, subcategory.title)
        : undefined;
      const visibleLinks = (subcategory.links || []).filter(
        link => !link.requiredPolicy || grantedPolicies[link.requiredPolicy] === true
      );

      if (!visibleLinks.length) {
        return;
      }

      if (subcategoryName) {
        abpRoutes.push({
          path: '',
          name: subcategoryName,
          parentName: categoryName,
          order: subcategoryOrder,
          invisible: false,
        });
      }

      visibleLinks.forEach((link, linkIndex) => {
        abpRoutes.push({
          path: link.url,
          name: createUniqueRouteName(routeNames, link.title),
          parentName: subcategoryName || categoryName,
          order: subcategoryOrder + linkIndex + 1,
          layout: eLayoutType.application,
          requiredPolicy: link.requiredPolicy,
        });
      });
    });
  });

  routes.add(abpRoutes);
}

function hasVisibleChildren(category: ICbdItem, grantedPolicies: Record<string, boolean>): boolean {
  return (category.subcategories || []).some(subcategory =>
    (subcategory.links || []).some(
      link => !link.requiredPolicy || grantedPolicies[link.requiredPolicy] === true
    )
  );
}

function createUniqueRouteName(routeNames: Map<string, number>, label = ''): string {
  const baseLabel = label;
  const currentCount = routeNames.get(baseLabel) || 0;

  routeNames.set(baseLabel, currentCount + 1);

  if (currentCount === 0) {
    return baseLabel;
  }

  return `${baseLabel}${'\u200B'.repeat(currentCount)}`;
}
